import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import C2S from 'canvas2svg';
import fileDownload from 'js-file-download';
import findIndex from 'lodash/findIndex';
import classNames from 'classnames';
import { makePoints, getArrowPoints, drawArrow, getFontInfo } from './util';
import questionImg from '../../../assets/images/question.svg';
import markImg from '../../../assets/images/mark.svg';
import DownloadIcon from '../../../components/icons/DownloadIcon';

const circleBgColor = '#312975';
const circleTextColor = 'white';
const influenceColor = '#D24B5A';
const dataColor = '#594FD1';
const fundingColor = '#FAB537';
const types = ['Funding', 'Data', 'Influence'];

const Diagram = () => {
  const [pixelRatio, setPixelRatio] = useState(window.devicePixelRatio);
  const [width, setWidth] = useState(
    window.innerWidth > 991 ? Math.min(800, window.innerWidth / 2) : window.innerWidth,
  );
  const [height, setHeight] = useState(800);
  const [polygonRadius, setPloyonRadius] = useState(250);
  const [circleRadius, setCircleRadius] = useState(60);
  const canvas = useRef(null);
  const cardRef = useRef(null);

  const stakeholders = useSelector((state) => state.stakeholder.stakeholders);
  const connections = useSelector((state) => state.connection.connections);
  const projects = useSelector((state) => state.project.projects);
  const project = useSelector((state) => state.project.project);

  React.useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth > 991 ? Math.min(800, window.innerWidth / 2) : window.innerWidth);
    }

    window.addEventListener('resize', handleResize);
  });

  const drawDiagram = (ctx) => {
    ctx.clearRect(0, 0, width, height);
    //drawStandard(ctx);
    //drawLogo(ctx);
    // if (projects.length) {
    //     drawProjectName(ctx);
    // }

    //console.log(project_id);
    if (!!stakeholders.length && project.id !== '') {
      const circleCenters = makePoints(
        width / 2 - 40,
        height / 2 - 40,
        polygonRadius,
        stakeholders.length,
        0,
      );

      if (stakeholders.length === 1) {
        setCircleRadius(50);
        drawCircle(ctx, width / 2 - 25, height / 2 - 25);
        const [textArray, fontSize] = getFontInfo(stakeholders[0].name, circleRadius);
        //ctx.font = `${fontSize}px Barlow Light`;
        //ctx.font = `${fontSize}px Barlow Black`;
        ctx.font = `${fontSize}px Barlow`;
        ctx.textAlign = 'center';
        ctx.fillStyle = circleTextColor;
        if (textArray.length === 1) {
          ctx.fillText(textArray[0], width / 2 - 25, height / 2 - 25);
        } else if (textArray.length === 2) {
          ctx.fillText(textArray[0], width / 2 - 25, height / 2 - 25 - fontSize / 2);
          ctx.fillText(textArray[1], width / 2 - 25, height / 2 - 25 + fontSize / 2);
        } else {
          ctx.fillText(textArray[0], width / 2 - 25, height / 2 - 25 - fontSize);
          ctx.fillText(textArray[1], width / 2 - 25, height / 2 - 25);
          ctx.fillText(textArray[2], width / 2 - 25, height / 2 - 25 + fontSize);
        }
        return;
      }

      const c1 = circleCenters[0];
      const c2 = circleCenters[1];

      //getting responsive circle radius
      const newRadius = (1 / 4) * Math.sqrt(Math.pow(c1.x - c2.x, 2) + Math.pow(c1.y - c2.y, 2));

      if (newRadius < 60) {
        setCircleRadius(newRadius);
      }

      circleCenters.forEach((center, index) => {
        drawCircle(ctx, center.x, center.y);
        // const text = stakeholders[index].name.length > 10 ? stakeholders[index].name.slice(0, 10) + '...' : stakeholders[index].name;
        const [textArray, fontSize] = getFontInfo(stakeholders[index].name, circleRadius);
        ctx.font = `${fontSize}px Barlow`;
        // ctx.font = `${fontSize}px Barlow Light`;
        // ctx.font = `20px Barlow Black`;
        ctx.textAlign = 'center';
        ctx.fillStyle = circleTextColor;

        if (textArray.length === 1) {
          ctx.fillText(textArray[0], center.x, center.y);
        } else if (textArray.length === 2) {
          ctx.fillText(textArray[0], center.x, center.y - fontSize / 2);
          ctx.fillText(textArray[1], center.x, center.y + fontSize / 2);
        } else {
          ctx.fillText(textArray[0], center.x, center.y - fontSize);
          ctx.fillText(textArray[1], center.x, center.y);
          ctx.fillText(textArray[2], center.x, center.y + fontSize);
        }
      });
      if (!!connections.length) {
        const rearrangedConnections = rearrangeConnection(connections);
        rearrangedConnections.forEach((con) => {
          if (
            findIndex(stakeholders, { id: con.from }) >= 0 &&
            findIndex(stakeholders, { id: con.to }) >= 0
          ) {
            const [startPoint, endPoint] = getArrowPoints(
              circleCenters[findIndex(stakeholders, { id: con.from })],
              circleCenters[findIndex(stakeholders, { id: con.to })],
              circleRadius + 5,
              con.repeated,
            );
            const Arrow = {
              startPoint,
              endPoint,
              arrowColor:
                con.type === 'Influence'
                  ? influenceColor
                  : con.type === 'Funding'
                  ? fundingColor
                  : dataColor,
            };
            drawArrow(
              ctx,
              Arrow.startPoint.x,
              Arrow.startPoint.y,
              Arrow.endPoint.x,
              Arrow.endPoint.y,
              3,
              1,
              50,
              25,
              Arrow.arrowColor,
              4,
            );
          }
        });
      }
    }
  };

  useEffect(() => {
    const ctx = canvas.current.getContext('2d');
    drawDiagram(ctx);
    if (cardRef.current) {
      const cardWidth = cardRef.current.offsetWidth;
      const canvasWidth = cardWidth;
      setWidth(canvasWidth);
      setHeight(canvasWidth);
      setPloyonRadius((canvasWidth / 2) * 0.7);
    }
  }, [projects, stakeholders, connections, drawDiagram]);

  const rearrangeConnection = (connections) => {
    const others = connections.map((con) => ({ ...con, repeated: 0 }));
    return others.map((connection) => {
      for (let i = 0; i < others.length; i += 1) {
        if (
          (others[i].from._id === connection.from._id &&
            others[i].to._id === connection.to._id &&
            others[i].repeated >= connection.repeated) ||
          (others[i].to._id === connection.from._id &&
            others[i].from._id === connection.to._id &&
            others[i].repeated >= connection.repeated)
        ) {
          connection.repeated++;
        }
      }
      return connection;
    });
  };

  const drawLogo = (ctx) => {
    const img = new Image();
    img.src = markImg; // can also be a remote URL e.g. http://
    img.onload = function () {
      ctx.drawImage(img, width - 200, height - 120, 200, 120);
    };
  };

  const drawStandard = (ctx) => {
    ctx.font = '18px Barlow Black';
    ctx.fillStyle = 'orange';
    ctx.fillText('Funding', 50, 15);
    ctx.fillStyle = '#594FD1';
    ctx.fillText('Data', 50, 40);
    ctx.fillStyle = 'red';
    ctx.fillText('Influence', 50, 65);

    drawArrow(ctx, 130, 10, 180, 10, 3, 1, 50, 20, 'orange', 4);
    drawArrow(ctx, 130, 35, 180, 35, 3, 1, 50, 20, '#594FD1', 4);
    drawArrow(ctx, 130, 60, 180, 60, 3, 1, 50, 20, 'red', 4);
  };

  const drawProjectName = (ctx) => {
    ctx.font = 'bolder 30px Barlow Black';
    ctx.fillStyle = '#312975';
    ctx.fillText(projects[0].name, width / 2, 30);
  };

  const drawCircle = (ctx, x, y) => {
    ctx.beginPath();
    ctx.arc(x, y, circleRadius, 0, 2 * Math.PI);
    ctx.fillStyle = circleBgColor;
    ctx.fill();
    ctx.strokeStyle = circleBgColor;
    ctx.stroke();
  };

  const dw = Math.floor(pixelRatio * width);
  const dh = Math.floor(pixelRatio * height);
  const style = { width, height };

  const exportPNG = () => {
    let downloadLink = document.createElement('a');
    downloadLink.setAttribute('download', `output-${Date.now()}.png`);
    const canvas = document.getElementById('diagram');
    let dataURL = canvas.toDataURL('image/png');
    let url = dataURL.replace(/^data:image\/png/, 'data:application/octet-stream');
    downloadLink.setAttribute('href', url);
    downloadLink.click();
  };

  const exportSVG = () => {
    const ctx = new C2S(dw, dh);
    drawDiagram(ctx);
    const svg = ctx.getSerializedSvg(true);
    fileDownload(svg, `output-${Date.now()}.svg`);
  };

  const renderTooltip = (props) => (
    <Tooltip id="custom-tooltip" {...props}>
      <div className="custom-tooltip">
        <div className="tooltip-header">Diagram!</div>
        <div className="tooltip-content">
          Here is where you can see the Funding Web and the flows of data, money, and influence
          between your stakeholders.
        </div>
      </div>
    </Tooltip>
  );

  return (
    <div className="medical-assistant">
      <div className="medical-assistant-header">
        <div className="left-section">
          <h1>{project.name}</h1>
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip}
          >
            <img src={questionImg} alt="quiz" />
          </OverlayTrigger>
        </div>
        <div className="right-section">
          <button className="outline-btn" disabled={project === ''} onClick={exportPNG}>
            <DownloadIcon color={project === '' ? '#DCDAFF' : '#594FD1'} />
            Export PNG
          </button>
          <button className="outline-btn" disabled={project === ''} onClick={exportSVG}>
            <DownloadIcon color={project === '' ? '#DCDAFF' : '#594FD1'} />
            Export SVG
          </button>
        </div>
      </div>
      <div className="medical-assistant-content">
        <div className="medical-assistant-content-header">
          {types.map((item) => (
            <div className="type-item" key={item}>
              <div
                className={classNames('inner-type', {
                  funding: item === 'Funding',
                  data: item === 'Data',
                  influence: item === 'Influence',
                })}
              ></div>
              <span>{item}</span>
            </div>
          ))}
        </div>
        <canvas id="diagram" ref={canvas} width={width} height={height} />
        <img className="logo" src={markImg} />
      </div>
    </div>
  );
};

export default Diagram;
